import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, delay, filter, repeatWhen, switchMap, take, takeUntil } from 'rxjs/operators';

import { AlertController } from '@ionic/angular';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ConfirmService } from '@core/services/confirm/confirm.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { NewAccountService } from '@core/services/new-products/new-account.service';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { ProductsService } from "@core/services/products/products.service";

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { ShortProductCard } from '@shared/models/short-product-card.model';

import { Steps } from '../models/step.model';
import { StoreService } from '../store/store.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-select-account-step',
    templateUrl: 'select-account-step.page.html',
    styleUrls: ['./select-account-step.page.scss', '../new-credit.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectAccountStepPage implements OnInit {
  title: string = 'Счета';
  subtitle: string = '';

  public availableAccounts: ShortProductCard[];
  public accountAgreeRules: any;
  public accountForm: FormGroup;
  public loaded$: Observable<boolean>;

  private accountActionsAlert = {
    title: 'Подтверждение!',
    message: `Отправить заявку в банк?`,
    buttons: [
      { text: 'Отменить', role: 'cancel' },
      {
        text: 'Отправить',
        handler: () => {
          this.accountActions();
        }
      }
    ],
    enableBackdropDismiss: false
  };

  constructor(
    private newAccountService: NewAccountService,
    private productsService: ProductsService,
    private store: StoreService,
    private stepManager: StepManagerService,
    private fb: FormBuilder,
    private navigationService: NavigationService,
    private alertCtrl: AlertController,
    private onlineCreditService: OnlineCreditService,
    private confirmService: ConfirmService
  ) {
    this.loaded$ = this.stepManager.stepLoaded$;
  }

  ngOnInit() {
    forkJoin(
      this.newAccountService.getNewAccountData("onlineCreditAccount"),
      this.productsService.getShortProductCardInBackOffice(
        this.store.value.application.branchId
      )
    ).pipe(
      untilDestroyed(this)
    ).subscribe(([acc, shorts]) => {
        if (!acc) this.stepManager.showErrorStepAlert("Отсутствуют доступные счета");
        this.accountAgreeRules = {
          enabled: acc.docSettings.find(s => s.name === "AGREE_RULES/ENABLED").value === "1",
          text: acc.docSettings.find(s => s.name === "AGREE_RULES/TEXT").value,
          url: acc.docSettings.find(s => s.name === "AGREE_RULES/URL").value
        };
        if (shorts && shorts.length) this.availableAccounts = shorts;
        this.accountForm = this.fb.group({
          accountAgreeRules: ['', [Validators.required, Validators.requiredTrue]],
          clientBranchId: ['', null],
          progContrId: ['', null]
        });
        this.accountForm.controls.progContrId.setValue(acc.accountTypes[0].id);
        this.accountForm.controls.clientBranchId.setValue(this.store.value.application.branchId);
        this.stepManager.setStepLoaded(true);
      },
      (err) => {
        this.stepManager.showErrorStepAlert(err);
      });
  }

  ionViewWillEnter() {
  }

  ionViewWillLeave() {}

  handleButtonClick() {
    this.alertCtrl.create(this.accountActionsAlert).then((alert) => alert.present());
  }

  accountActions() {
    if (!this.availableAccounts || !this.availableAccounts.length) return this.openNewAccount();

    this.stepManager.setStepLoaded(false);
    this.onlineCreditService
      .sendOnlineCreditRequest(this.store.value.application)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          void this.navigationService.push('/new-product/credit/sign-final-document-pack', { replaceUrl: true });
          // this.stepManager.changeStep(Steps.signFinalDocumentPack);
        },
        (err) => {
          this.stepManager.showErrorStepAlert(err);
        }
      );
  }

  openNewAccount() {
    if (this.accountForm.invalid) return;
    this.stepManager.setStepLoaded(false);
    const data = this.accountForm.value;
    this.newAccountService
      .createNewAccount(data)
      .pipe(
        catchError((err) => {
          if (err['errorCode']) return of(new Error(err['errorMessage'] || 'Ошибка открытия счета!'));
          return err;
        }),
        switchMap(({ id }) => this.confirmService.confirmDocument(id)),
        catchError((_) => of(undefined)),
        switchMap((_) => this.checkAvailableAccounts()),
        catchError((_) => of(undefined)),
        switchMap((res: ShortProductCard[]) => {
          if (res && res.length) {
            this.store.value.application.accId = res[0].accID;
            this.store.value.application.accNumber = res[0].accNumber;
            // this.store.updateApplication({accId: res[0].accID, accNumber: res[0].accNumber});
            return this.onlineCreditService.sendOnlineCreditRequest(this.store.value.application);
          }
          this.stepManager.showErrorStepAlert();
        }),
        catchError((_) => of(undefined)),
        untilDestroyed(this)
      )
      .subscribe(
        (res) => {
          void this.navigationService.push('/new-product/credit/sign-final-document-pack', { replaceUrl: true });
          // this.stepManager.changeStep(Steps.signFinalDocumentPack)
        },
        () => {
          this.stepManager.showOfflineAlert();
        }
      );
  }

  private checkAvailableAccounts(): Observable<ShortProductCard[]> {
    return this.productsService.getShortProductCardInBackOffice(this.store.value.application.branchId).pipe(
      repeatWhen(res => res.pipe(delay(3000))),
      filter((data: ShortProductCard[]) => {
        return data.length > 0;
      }),
      take(1)
    );
  }

  setAccount(id) {
    const acc = this.availableAccounts.find((acc) => acc.id === id);
    this.store.value.application.accId = acc.accID;
    this.store.value.application.accNumber = acc.accNumber;
  }
}
